import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme } from '@mui/material/styles';

// Predefined themes
const predefinedThemes = {
  bookmyop: {
    id: 'bookmyop',
    name: 'BookMyOP Teal',
    isPredefined: true,
    mode: 'light',
    palette: {
      primary: { main: '#6bbda3', light: '#83c9b2', dark: '#1e6985' },
      secondary: { main: '#49869c', light: '#bbe1d7', dark: '#70a2b2' },
      background: { default: '#f5fffb', paper: '#f0f9f7' },
      text: { primary: '#1e6985', secondary: '#49869c' },
    },
    typography: {
      fontFamily: 'Sansation, Inter, sans-serif',
    },
  },
};

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    const saved = localStorage.getItem('bookmyop-theme');
    return saved ? JSON.parse(saved) : predefinedThemes.bookmyop;
  });

  const [customThemes, setCustomThemes] = useState(() => {
    const saved = localStorage.getItem('bookmyop-custom-themes');
    return saved ? JSON.parse(saved) : {};
  });

  const [customColors, setCustomColors] = useState(() => {
    const saved = localStorage.getItem('bookmyop-custom-colors');
    return saved ? JSON.parse(saved) : {
      primary: '#6bbda3',
      secondary: '#f5fffb',
      background: '#f5fffb',
      text: '#1e6985',
    };
  });

  const [selectedFont, setSelectedFont] = useState(() => {
    const saved = localStorage.getItem('bookmyop-font');
    return saved || 'Sansation';
  });

  // Combine predefined and custom themes
  const getAllThemes = () => {
    return { ...predefinedThemes, ...customThemes };
  };

  const availableFonts = [
    { name: 'Sansation', value: 'Sansation, Inter, sans-serif' },
    { name: 'Open Sans', value: 'Open Sans, Lato, sans-serif' },
    { name: 'Roboto', value: 'Roboto, Open Sans, sans-serif' },
    { name: 'Lato', value: 'Lato, Open Sans, sans-serif' },
    { name: 'Source Sans Pro', value: 'Source Sans Pro, Inter, sans-serif' },
    { name: 'Lora', value: 'Lora, Merriweather, serif' },
    { name: 'Playfair Display', value: 'Playfair Display, Lora, serif' },
    { name: 'Merriweather', value: 'Merriweather, Lora, serif' },
  ];

  const createCustomTheme = (themeConfig) => {
    return createTheme({
      palette: {
        mode: themeConfig.mode || 'light', // Use the mode from the themeConfig, default to light
        primary: {
          main: themeConfig.palette.primary.main,
          light: themeConfig.palette.primary.light,
          dark: themeConfig.palette.primary.dark,
        },
        secondary: {
          main: themeConfig.palette.secondary.main,
          light: themeConfig.palette.secondary.light,
          dark: themeConfig.palette.secondary.dark,
        },
        background: {
          default: themeConfig.palette.background.default,
          paper: themeConfig.palette.background.paper,
        },
        text: {
          primary: themeConfig.palette.text.primary,
          secondary: themeConfig.palette.text.secondary,
        },
      },
      typography: {
        fontFamily: themeConfig.typography.fontFamily,
        h1: {
          fontFamily: 'Sansation, Inter, sans-serif',
          fontSize: '2rem',
          fontWeight: 600,
          letterSpacing: '-0.01em',
          '@media (min-width:600px)': {
            fontSize: '2.5rem',
          },
        },
        h2: {
          fontFamily: themeConfig.typography.fontFamily.split(',')[0].trim(),
          fontSize: '1.5rem',
          fontWeight: 600,
          letterSpacing: '-0.01em',
          '@media (min-width:600px)': {
            fontSize: '2rem',
          },
        },
        h3: {
          fontFamily: themeConfig.typography.fontFamily.split(',')[0].trim(),
          fontSize: '1.25rem',
          fontWeight: 600,
          letterSpacing: '-0.01em',
          '@media (min-width:600px)': {
            fontSize: '1.5rem',
          },
        },
        h4: {
          fontFamily: themeConfig.typography.fontFamily.split(',')[0].trim(),
          fontSize: '1.125rem',
          fontWeight: 600,
          letterSpacing: '-0.01em',
        },
        body1: {
          fontFamily: themeConfig.typography.fontFamily.split(',')[0].trim(),
          fontSize: '1rem',
          lineHeight: 1.6,
        },
        body2: {
          fontFamily: themeConfig.typography.fontFamily.split(',')[0].trim(),
          fontSize: '0.875rem',
          lineHeight: 1.6,
        },
        button: {
          fontFamily: themeConfig.typography.fontFamily.split(',')[0].trim(),
          fontWeight: 500,
        },
        caption: {
          fontFamily: themeConfig.typography.fontFamily.split(',')[1]?.trim() || 'Inter',
          fontSize: '0.75rem',
          lineHeight: 1.5,
        },
      },
      shape: {
        borderRadius: 12,
      },
      transitions: {
        easing: {
          easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
          sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
        },
        duration: {
          shortest: 150,
          shorter: 200,
          short: 250,
          standard: 300,
          complex: 375,
          enteringScreen: 225,
          leavingScreen: 195,
        },
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              borderRadius: 12,
              padding: '8px 16px',
              fontSize: '0.9375rem',
              fontWeight: 600,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
              },
            },
            contained: {
              background: ({ theme }) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: ({ theme }) => `0 4px 20px ${theme.palette.primary.main}40`,
              color: 'white',
              '&:hover': {
                background: ({ theme }) => `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                boxShadow: ({ theme }) => `0 6px 25px ${theme.palette.primary.main}59`,
              },
            },
            outlined: {
              borderWidth: '1.5px',
              '&:hover': {
                borderWidth: '1.5px',
                background: ({ theme }) => `${theme.palette.primary.main}0d`,
              },
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              background: ({ theme }) => theme.palette.background.paper,
              backdropFilter: 'blur(12px)',
              borderBottom: ({ theme }) => `1px solid ${theme.palette.primary.main}1a`,
              boxShadow: ({ theme }) => `0 4px 30px ${theme.palette.primary.main}1a`,
              color: '#102A43',
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 16,
              background: ({ theme }) => theme.palette.background.paper,
              backdropFilter: 'blur(10px)',
              border: ({ theme }) => `1px solid ${theme.palette.primary.main}1a`,
              boxShadow: ({ theme }) => `0 4px 30px ${theme.palette.primary.main}1a`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: ({ theme }) => `0 8px 40px ${theme.palette.primary.main}33`,
              },
            },
          },
        },
        MuiBottomNavigation: {
          styleOverrides: {
            root: {
              height: 84,
              backgroundColor: 'transparent',
              padding: '8px',
              '& .MuiBottomNavigationAction-root': {
                color: '#6B7280',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                padding: '8px 12px',
                minWidth: 'auto',
                '&.Mui-selected': {
                  '& .MuiBottomNavigationAction-label': {
                    fontSize: '0.75rem',
                    background: ({ theme }) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 600,
                  },
                  '& svg': {
                    transform: 'scale(1.2)',
                    filter: ({ theme }) => `drop-shadow(0 2px 4px ${theme.palette.primary.main}33)`,
                  },
                },
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.7rem',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                marginTop: '4px',
              },
            },
          },
        },
        MuiMenu: {
          styleOverrides: {
            paper: {
              background: ({ theme }) => theme.palette.background.paper,
              backdropFilter: 'blur(12px)',
              borderRadius: 16,
              border: ({ theme }) => `1.5px solid ${theme.palette.primary.main}1a`,
              boxShadow: ({ theme }) => `0 4px 30px ${theme.palette.primary.main}1a`,
              '& .MuiMenuItem-root': {
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                borderRadius: 8,
                margin: '4px',
                padding: '12px',
                '&:hover': {
                  background: ({ theme }) => `linear-gradient(135deg, ${theme.palette.primary.main}1a 0%, ${theme.palette.primary.dark}1a 100%)`,
                },
              },
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
            },
            elevation1: {
              boxShadow: ({ theme }) => `0 1px 3px ${theme.palette.primary.main}1a, 0 1px 2px -1px ${theme.palette.primary.main}1a`,
            },
            elevation8: {
              boxShadow: ({ theme }) => `0 4px 24px -1px ${theme.palette.primary.main}1a, 0 0 16px -4px ${theme.palette.primary.main}1a`,
            },
          },
        },
        MuiIcon: {
          styleOverrides: {
            root: {
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            },
          },
        },
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              backgroundColor: ({ theme }) => theme.palette.background.default,
              color: ({ theme }) => theme.palette.text.primary,
            },
            '#root': {
              backgroundColor: ({ theme }) => theme.palette.background.default,
            },
            '.app-container': {
              backgroundColor: ({ theme }) => theme.palette.background.default,
            },
          },
        },
      },
    });
  };

  const theme = createCustomTheme(currentTheme);

  const setTheme = (themeKey) => {
    const allThemes = getAllThemes();
    const newTheme = allThemes[themeKey];
    if (newTheme) {
      setCurrentTheme(newTheme);
      localStorage.setItem('bookmyop-theme', JSON.stringify(newTheme));
    }
  };

  const updateCustomColors = (newColors) => {
    setCustomColors(newColors);
    localStorage.setItem('bookmyop-custom-colors', JSON.stringify(newColors));
    
    const updatedTheme = {
      ...currentTheme,
      palette: {
        ...currentTheme.palette,
        primary: { 
          main: newColors.primary,
          light: newColors.primary + '80',
          dark: newColors.primary + 'CC'
        },
        secondary: { 
          main: newColors.secondary,
          light: newColors.secondary + '80',
          dark: newColors.secondary + 'CC'
        },
        background: { 
          default: newColors.background,
          paper: newColors.background + 'F0'
        },
        text: { 
          primary: newColors.text,
          secondary: newColors.text + '80'
        },
      },
    };
    
    setCurrentTheme(updatedTheme);
    localStorage.setItem('bookmyop-theme', JSON.stringify(updatedTheme));
  };

  const updateFont = (fontName) => {
    setSelectedFont(fontName);
    localStorage.setItem('bookmyop-font', fontName);
    
    const fontConfig = availableFonts.find(f => f.name === fontName);
    if (fontConfig) {
      const updatedTheme = {
        ...currentTheme,
        typography: {
          ...currentTheme.typography,
          fontFamily: fontConfig.value,
        },
      };
      
      setCurrentTheme(updatedTheme);
      localStorage.setItem('bookmyop-theme', JSON.stringify(updatedTheme));
    }
  };

  const resetToDefault = () => {
    setCurrentTheme(predefinedThemes.bookmyop);
    setCustomColors({
      primary: '#6bbda3',
      secondary: '#49869c',
      background: '#f5fffb',
      text: '#1e6985',
    });
    setSelectedFont('Sansation');
    localStorage.setItem('bookmyop-theme', JSON.stringify(predefinedThemes.bookmyop));
    localStorage.setItem('bookmyop-custom-colors', JSON.stringify({
      primary: '#6bbda3',
      secondary: '#49869c',
      background: '#f5fffb',
      text: '#1e6985',
    }));
    localStorage.setItem('bookmyop-font', 'Sansation');
  };

  // Add new custom theme
  const addCustomTheme = (themeData) => {
    const newThemeId = `custom_${Date.now()}`;
    const newTheme = {
      ...themeData,
      id: newThemeId,
      isPredefined: false,
      mode: themeData.mode || 'light', // Ensure mode is always set
    };
    
    const updatedCustomThemes = { ...customThemes, [newThemeId]: newTheme };
    setCustomThemes(updatedCustomThemes);
    localStorage.setItem('bookmyop-custom-themes', JSON.stringify(updatedCustomThemes));
    
    return newThemeId;
  };

  // Edit custom theme
  const editCustomTheme = (themeId, updatedThemeData) => {
    if (customThemes[themeId]) {
      const updatedTheme = {
        ...updatedThemeData,
        id: themeId,
        isPredefined: false,
        mode: updatedThemeData.mode || 'light', // Ensure mode is always set
      };
      
      const updatedCustomThemes = { ...customThemes, [themeId]: updatedTheme };
      setCustomThemes(updatedCustomThemes);
      localStorage.setItem('bookmyop-custom-themes', JSON.stringify(updatedCustomThemes));
      
      // If this is the current theme, update it
      if (currentTheme.id === themeId) {
        setCurrentTheme(updatedTheme);
        localStorage.setItem('bookmyop-theme', JSON.stringify(updatedTheme));
      }
    }
  };

  // Delete custom theme
  const deleteCustomTheme = (themeId) => {
    if (customThemes[themeId]) {
      const updatedCustomThemes = { ...customThemes };
      delete updatedCustomThemes[themeId];
      setCustomThemes(updatedCustomThemes);
      localStorage.setItem('bookmyop-custom-themes', JSON.stringify(updatedCustomThemes));
      
      // If this is the current theme, switch to default
      if (currentTheme.id === themeId) {
        setCurrentTheme(predefinedThemes.bookmyop);
        localStorage.setItem('bookmyop-theme', JSON.stringify(predefinedThemes.bookmyop));
      }
    }
  };

  // Save current theme as custom theme
  const saveCurrentAsCustom = (themeName) => {
    const themeData = {
      name: themeName,
      mode: currentTheme.mode || 'light',
      palette: currentTheme.palette,
      typography: currentTheme.typography,
    };
    
    return addCustomTheme(themeData);
  };

  const value = {
    theme,
    currentTheme,
    predefinedThemes,
    customThemes,
    getAllThemes,
    customColors,
    selectedFont,
    availableFonts,
    setTheme,
    updateCustomColors,
    updateFont,
    resetToDefault,
    addCustomTheme,
    editCustomTheme,
    deleteCustomTheme,
    saveCurrentAsCustom,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}; 